import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, SearchIcon, XIcon } from 'lucide-react'
import { useMemo } from 'react'
import { ScreenHeader } from '@app/components/screen-header'
import { Button } from '@app/components/ui/button'
import { Card } from '@app/components/ui/card'
import { Input } from '@app/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@app/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { cn } from '@app/lib/cn'
import { useCsvDownload } from '@app/lib/download'
import { useUrlSearchInput } from '@app/lib/nav'
import type { OrderTypeParam } from '@app/router'
import { useCanViewPurchasing } from '@app/features/auth/api'
import { ordersApi, ordersQueryKeys } from '@app/features/orders/api'
import { daysAgo, formatAmount, formatDate, formatQty } from '@app/features/orders/format'
import type {
  OrderFilter,
  OrderListRow,
  OrderListSummary,
  OrderSort,
  OrderType,
} from '@app/features/orders/types'
import {
  DueDateCell,
  EmptyState,
  ErrorState,
  SampleDot,
  StatusBadge,
  ShipBar,
  TableSkeleton,
  TypeBadge,
} from '@app/features/orders/ui'

/**
 * Order list.
 *
 * Korgun's "Pazarlama Analiz ve Sipariş Yönetimi" screen makes you fill in a
 * form first and only then shows the result in another tab (docs/korgun-screens.md
 * §4, pain point #1). Here it's the opposite: the order book loads the moment
 * the screen opens — default sort is "orders with work remaining first,
 * newest to oldest within that" — filters sit next to the table as live chips.
 *
 * Every filter is a URL search param (see `validateSearch` in router.tsx), so a
 * view worth showing someone is a link, and Back undoes a filter instead of
 * leaving the screen.
 */

const route = getRouteApi('/_korumali/siparisler')

const PAGE_SIZE = 50

const STATUS_CHIPS: { value: OrderFilter; label: string; key: keyof OrderListSummary }[] = [
  { value: 'all', label: 'Tümü', key: 'all' },
  { value: 'open', label: 'Açık', key: 'open' },
  { value: 'overdue', label: 'Geciken', key: 'overdue' },
  { value: 'pending', label: 'Sevk bekleyen', key: 'pending' },
  { value: 'partial', label: 'Kısmi sevk', key: 'partial' },
  { value: 'complete', label: 'Sevk edilen', key: 'complete' },
  { value: 'closed', label: 'Kapalı', key: 'closed' },
]

/**
 * Document type. `sales` (satış + numune) is the default because purchase
 * orders have their own module now — listing them here too made the order book
 * read 563 orders heavier than the sales book actually is. "Satınalma" is still
 * one click away, and it lands on the same rows the Satınalma screen shows —
 * for a role that may open that module. Without it the option is gone, and so
 * is "Tüm tipler" (which would mean the same thing); see `canSeePurchase`.
 *
 * No per-option counts (e.g. "Yalnız numune (42)"): `GET /api/orders`'s
 * `summary` is computed under the ALREADY-selected type filter (see
 * `orders.ts`, the `summaryPromise` query includes `type` in its `base`
 * conditions), so it can only ever answer "how many under the current type",
 * never "how many per type" for the other options in this list. That needs a
 * `SipTip`-grouped count the API doesn't return — out of scope here since it's
 * a worker change; flagging for a follow-up instead of adding one.
 */
const TYPE_OPTIONS: { value: OrderTypeParam; label: string }[] = [
  { value: 'sales', label: 'Satış + numune' },
  { value: 'S', label: 'Yalnız satış' },
  { value: 'ST', label: 'Yalnız numune' },
  { value: 'A', label: 'Satınalma' },
  { value: 'all', label: 'Tüm tipler' },
]

const SORT_OPTIONS: { value: OrderSort; label: string }[] = [
  { value: 'priority', label: 'Önce açık işler' },
  { value: 'date', label: 'Sipariş tarihi' },
  { value: 'dueDate', label: 'Termin tarihi' },
  { value: 'amount', label: 'Tutar (TL)' },
]

/** Rolling windows; `null` = every date, and stays out of the URL. */
const DATE_PRESETS: { value: string; label: string; days: 30 | 90 | 365 | null }[] = [
  { value: 'all', label: 'Tüm tarihler', days: null },
  { value: '30', label: 'Son 30 gün', days: 30 },
  { value: '90', label: 'Son 90 gün', days: 90 },
  { value: '365', label: 'Son 1 yıl', days: 365 },
]

/**
 * Column priority.
 *
 * The shell keeps a 224px sidebar from `md` up, so the table itself gets about
 * 570px at 834 (iPad portrait) and 936px at 1200 (the laptops in the office).
 * Sized against those two numbers: Adet, Kalan, Sevkiyat and the order number
 * never leave, Tutar and Termin come back at 1024, and the columns that are
 * near-constant under the default filter (Tip — almost always "Satış" now) or
 * derivable (Sevk = Adet − Kalan) are the first to go.
 */
const COLUMN = {
  /** Order date: only once there is room for it next to Termin. */
  date: 'hidden xl:table-cell',
  /** Type badge — the type filter defaults to satış+numune, so it barely varies. */
  type: 'hidden xl:table-cell',
  /** Shipped qty: Adet − Kalan gives the same number. */
  shipped: 'hidden 2xl:table-cell',
  qty: 'hidden sm:table-cell',
  due: 'hidden lg:table-cell',
  amount: 'hidden lg:table-cell',
} as const

export function OrdersScreen() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const csv = useCsvDownload()

  /*
   * Purchase orders are satınalma records, so the same capability decides here:
   * the API refuses `type=A` from an account without it and reads `type=all` as
   * "every type you may see" (see `typeForRole` in worker/api/orders.ts). The
   * screen follows — those two options leave the list, and a stale link
   * carrying one of them falls back to the default instead of showing an error
   * card for a filter the user cannot even see any more.
   */
  const canSeePurchase = useCanViewPurchasing()
  const typeOptions = canSeePurchase
    ? TYPE_OPTIONS
    : TYPE_OPTIONS.filter((option) => option.value !== 'A' && option.value !== 'all')

  // Defaults live here, not in the URL — an untouched screen stays at /siparisler.
  const query = search.query ?? ''
  const status = search.status ?? 'all'
  const requestedType = search.type ?? 'sales'
  const type =
    !canSeePurchase && (requestedType === 'A' || requestedType === 'all')
      ? 'sales'
      : requestedType
  const days = search.days ?? null
  const sort = search.sort ?? 'priority'
  const page = search.page ?? 1

  /**
   * Writes one or more filters and returns to page 1 — changing a filter while
   * on page 7 otherwise lands on an empty page. Pushes a history entry, so Back
   * undoes the filter rather than leaving the screen.
   */
  // Filter clicks keep the viewport still and carry any text waiting on the
  // search debounce — a click inside the 300 ms window used to discard it.
  function setFilters(next: Partial<typeof search>) {
    const text = flushSearch({ commit: false })
    void navigate({
      search: (prev) => ({
        ...prev,
        query: text === '' ? undefined : text,
        ...next,
        page: undefined,
      }),
      resetScroll: false,
    })
  }

  /*
   * The box keeps its own copy so typing never waits for a navigation, and
   * lands in the URL 300 ms later — `replace`, so a ten-character search leaves
   * one history entry instead of ten. Back/forward and "Filtreleri temizle"
   * move the URL from outside and the box follows.
   */
  const [searchInput, setSearchInput, flushSearch] = useUrlSearchInput(query, (text) => {
    void navigate({
      search: (prev) => ({ ...prev, query: text === '' ? undefined : text, page: undefined }),
      replace: true,
      // The commit fires mid-typing — a scroll reset would yank the viewport
      // away from the box (see the üretim list for the full story).
      resetScroll: false,
    })
  })

  const from = useMemo(() => (days === null ? null : daysAgo(days)), [days])

  const params = useMemo(
    () => ({
      query,
      status,
      /*
       * `sales` means SipTip in ('S','ST') — sales orders plus samples.
       *
       * Server side: /api/orders and /api/orders/export accept type=sales
       * (SipTip in ('S','ST')), so this default excludes purchase orders.
       */
      type: type as OrderType | 'all',
      from,
      sort,
      page,
      pageSize: PAGE_SIZE,
    }),
    [query, status, type, from, sort, page],
  )

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: ordersQueryKeys.list(params),
    queryFn: () => ordersApi.list(params),
    placeholderData: keepPreviousData,
  })

  const rows = data?.rows ?? []
  const summary = data?.summary
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1
  /*
   * Because of `keepPreviousData`, `data` still carries the OLD page while the
   * next one loads; computing the range label from local `page` then labeled
   * stale rows with the new page's numbers. As in the stock/production
   * screens, page info is read from the response.
   */
  const serverPage = data?.page ?? page
  const serverPageSize = data?.pageSize ?? PAGE_SIZE
  const first = total === 0 ? 0 : (serverPage - 1) * serverPageSize + 1
  const last = Math.min(serverPage * serverPageSize, total)
  const hasFilters = query !== '' || status !== 'all' || type !== 'sales' || days !== null

  /*
   * ST rows never ship and never reach production (see CLAUDE.md — Siparis_Kay.SipTip),
   * so under type=ST every Sevk/Kalan/Sevk durumu column reads as "nothing
   * happened" — without this, that looks broken rather than expected.
   */
  const description =
    type === 'ST'
      ? 'Numune siparişleri — sevk edilmez, üretime düşmez.'
      : 'Sipariş defteri, sevkiyat durumu ve termin takibi — açık işler üstte.'

  /** Page 1 is the default, so it leaves the URL entirely. */
  function goToPage(next: number) {
    void navigate({ search: (prev) => ({ ...prev, page: next <= 1 ? undefined : next }) })
  }

  /**
   * Back to the default view: every param drops out of the URL, and the search
   * box follows it. (Clearing the box here as well would only queue a second,
   * identical navigation 300 ms later.)
   */
  function clearFilters() {
    void navigate({
      search: (prev) => ({
        ...prev,
        query: undefined,
        status: undefined,
        type: undefined,
        days: undefined,
        page: undefined,
      }),
      resetScroll: false,
    })
  }

  return (
    <>
      <ScreenHeader
        title="Siparişler"
        description={description}
        actions={
          <span className="font-mono text-micro tnum text-ink-muted">
            {isPending ? 'yükleniyor…' : `${formatQty(total)} kayıt`}
          </span>
        }
      />

      <Card>
        {/* ── Filters: sit above the table as live controls, not a form ── */}
        <div className="flex flex-col gap-2.5 border-b border-hairline px-3.5 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-52 flex-1 sm:max-w-xs">
              <SearchIcon
                className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-muted"
                aria-hidden
              />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Müşteri, sipariş no ya da belge no"
                aria-label="Siparişlerde ara"
                className="pl-7"
              />
              {searchInput ? (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  aria-label="Aramayı temizle"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-xs p-0.5 text-ink-muted hover:text-ink"
                >
                  <XIcon className="size-3.5" aria-hidden />
                </button>
              ) : null}
            </div>

            <Select
              value={type}
              onValueChange={(v) => setFilters({ type: v === 'sales' ? undefined : (v as OrderTypeParam) })}
            >
              <SelectTrigger className="w-40" aria-label="Sipariş tipi">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={days === null ? 'all' : String(days)}
              onValueChange={(v) =>
                setFilters({ days: DATE_PRESETS.find((p) => p.value === v)?.days ?? undefined })
              }
            >
              <SelectTrigger className="w-36" aria-label="Tarih aralığı">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_PRESETS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={sort}
              onValueChange={(v) => setFilters({ sort: v === 'priority' ? undefined : (v as OrderSort) })}
            >
              <SelectTrigger className="w-40" aria-label="Sıralama">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Exports the whole filtered order book, not just the page on screen. */}
            <Button
              variant="outline"
              disabled={csv.busy}
              aria-busy={csv.busy}
              onClick={() =>
                csv.start('/orders/export', { query: flushSearch(), status, type, from, sort })
              }
            >
              <DownloadIcon aria-hidden />
              {csv.busy ? 'Hazırlanıyor…' : 'Dışa aktar'}
            </Button>

            {csv.error ? (
              <span role="alert" className="text-micro text-hardal">
                {csv.error}
              </span>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-1">
            {STATUS_CHIPS.map((chip) => {
              const active = status === chip.value
              const count = summary?.[chip.key]
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => setFilters({ status: chip.value === 'all' ? undefined : chip.value })}
                  aria-pressed={active}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-xs border px-2 py-1 text-micro transition-colors',
                    active
                      ? 'border-kordovan/30 bg-kordovan/8 font-medium text-kordovan'
                      : 'border-hairline bg-surface text-ink-muted hover:border-ink-muted/40 hover:text-ink',
                  )}
                >
                  {chip.label}
                  <span className={cn('font-mono tnum', active ? 'text-kordovan' : 'text-ink-muted/70')}>
                    {count === undefined ? '·' : formatQty(count)}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        <TableScroller className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Sipariş</TableHead>
                <TableHead className={cn('w-24', COLUMN.date)}>Tarih</TableHead>
                <TableHead className="min-w-36">Müşteri</TableHead>
                <TableHead className={cn('w-28', COLUMN.type)}>Tip</TableHead>
                <TableHead className={cn('w-28', COLUMN.due)}>Termin</TableHead>
                <TableHead className={cn('w-18 text-right', COLUMN.qty)}>Adet</TableHead>
                <TableHead className={cn('w-20 text-right', COLUMN.shipped)}>Sevk</TableHead>
                <TableHead className="w-18 text-right">Kalan</TableHead>
                {/* Named for what it now carries: the bar AND the open/closed state. */}
                <TableHead className="w-32">Sevk durumu</TableHead>
                <TableHead className={cn('w-28 text-right', COLUMN.amount)}>Tutar</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isPending ? (
                <TableSkeleton rows={10} cols={10} />
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <ErrorState
                      message={error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu.'}
                      onRetry={() => void refetch()}
                    />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <EmptyState
                      title="Bu filtreyle sipariş yok"
                      description={
                        hasFilters
                          ? 'Arama ya da durum filtrelerini gevşetip yeniden bakın.'
                          : 'Sipariş defteri boş görünüyor. Korgun aynası güncel mi kontrol edin.'
                      }
                      action={
                        hasFilters ? (
                          <Button variant="primary" onClick={clearFilters}>
                            Filtreleri temizle
                          </Button>
                        ) : null
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.sipNo} className="relative">
                    <TableCell className="font-mono">
                      {/* The whole row is the link — instead of Korgun's record arrows. */}
                      <span className="inline-flex items-center gap-1">
                        <Link
                          to="/siparisler/$sipNo"
                          params={{ sipNo: String(row.sipNo) }}
                          className="font-medium text-ink after:absolute after:inset-0 after:content-[''] hover:text-kordovan"
                        >
                          {row.sipNo}
                        </Link>
                        {/* Tip column hides below xl (COLUMN.type) — this stays
                            visible at every width so an ST row never reads as
                            a sales order just because the screen is narrow. */}
                        {row.type === 'ST' ? <SampleDot /> : null}
                      </span>
                    </TableCell>

                    <TableCell className={cn('font-mono tnum text-ink-muted', COLUMN.date)}>
                      {formatDate(row.orderDate)}
                    </TableCell>

                    {/* max-w-0 is what lets the column give way: without it the
                        nowrap of `truncate` sets the column's minimum to the
                        longest customer name and pushes the table into scroll. */}
                    <TableCell className="max-w-0">
                      <span className="block truncate font-medium text-ink">{row.cariName}</span>
                      <span className="block truncate font-mono text-micro text-ink-muted">
                        {row.cariKod}
                        {row.documentNo ? ` · ${row.documentNo}` : ''}
                      </span>
                    </TableCell>

                    <TableCell className={COLUMN.type}>
                      <TypeBadge type={row.type} typeName={row.typeName} />
                    </TableCell>

                    <TableCell className={COLUMN.due}>
                      <DueDateCell dateText={formatDate(row.deliveryDate)} overdueDays={row.overdueDays} />
                    </TableCell>

                    <TableNumCell className={COLUMN.qty}>{formatQty(row.orderedQty)}</TableNumCell>

                    <TableNumCell className={cn('text-ink-muted', COLUMN.shipped)}>
                      {formatQty(row.shippedQty)}
                    </TableNumCell>

                    <TableNumCell className={cn(row.kalanQty > 0 ? 'font-medium text-ink' : 'text-ink-muted/60')}>
                      {row.kalanQty > 0 ? formatQty(row.kalanQty) : '—'}
                    </TableNumCell>

                    {/* The status column that never leaves. Korgun Durum='*' doesn't
                        mean shipped — some orders are closed by hand with a remainder
                        — so "Kapalı" rides alongside the bar rather than replacing it. */}
                    <TableCell>
                      <span className="flex items-center gap-1.5">
                        <ShipBar percent={row.percent} status={row.shipStatus} overdue={row.overdue} />
                        {row.status === 'closed' ? <StatusBadge status={row.status} /> : null}
                      </span>
                    </TableCell>

                    <TableNumCell className={COLUMN.amount}>
                      <AmountCell row={row} />
                    </TableNumCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableScroller>

        {!isPending && !isError && rows.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline px-3.5 py-2">
            <span className="font-mono text-micro tnum text-ink-muted">
              {formatQty(first)}–{formatQty(last)} / {formatQty(total)}
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                <ChevronLeftIcon aria-hidden />
                Önceki
              </Button>
              <span className="px-1 font-mono text-micro tnum text-ink-muted">
                {formatQty(serverPage)} / {formatQty(totalPages)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => goToPage(Math.min(totalPages, page + 1))}
              >
                Sonraki
                <ChevronRightIcon aria-hidden />
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      <p className="mt-2 text-micro text-ink-muted">
        Sevkiyat yüzdesi Korgun'un <code className="font-mono">Si_GCHar</code> sipariş defterinden
        hesaplanır; alış siparişlerinde giren/çıkan sütunları ters çalıştığı için yön düzeltilmiştir.
      </p>
    </>
  )
}

/**
 * Amount cell.
 *
 * Orders whose lines carry different currencies (22 records) have no single
 * total: the per-currency breakdown is stacked below, with the TL equivalent
 * shown on the main line. It used to all be summed and labelled with
 * `min(ParaCinsi)`.
 */
function AmountCell({ row }: { row: OrderListRow }) {
  if (row.mixedCurrency) {
    const breakdown = row.currencyTotals.filter((p) => p.amount !== 0)
    return (
      <span
        className="flex flex-col items-end leading-tight"
        title="Sipariş satırlarında birden fazla para birimi var; toplamlar ayrı ayrı gösterilir."
      >
        <span>{row.amountTl === null ? '—' : formatAmount(row.amountTl, 'TL')}</span>
        <span className="text-micro text-hardal">
          {breakdown.length > 0
            ? breakdown.map((p) => formatAmount(p.amount, p.currency)).join(' + ')
            : 'karışık para birimi'}
        </span>
      </span>
    )
  }

  if (row.amount === null || row.amount === 0) {
    return <span className="text-ink-muted/60">—</span>
  }

  return <span>{formatAmount(row.amount, row.currency)}</span>
}
